import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { SmileOutlined, SmileTwoTone } from '@ant-design/icons';
import { IBookingRound } from '../../types/models/lodging';
import { useTheme } from "../../contexts/ThemeProvider";

interface IAssignment {
  id: number;
  assignment_round: number;
  unit_owner: number;
  fraction: number;
  checkin: string;
  checkout: string;
  week: number;
  preferred: boolean;
  preferred_rank: number;
  rank: number;
  price_avg: number;
  round: number;
}

interface IHouseOwner {
  id: number;
  fraction_numbers: number[];
  owner: any;
}

interface BookingRoundCalendarViewProps {
  bookingRound: IBookingRound | undefined;
  assignments: IAssignment[];
  houseOwners: IHouseOwner[];
  fractionsCount: number;
  selectedAssignments: IAssignment[];
  setSelectedAssignments: (assignments: IAssignment[]) => void;
}

const ViewContainer = styled.div`
  display: flex;
  gap: 24px;
`;

const CalendarContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  flex: 1;
  justify-content: center;
`;

const LegendContainer = styled.div`
  width: 200px;
  padding: 8px;
  // border: 1px solid #f0f0f0;
  border-radius: 4px;
  height: fit-content;
  position: sticky;
  top: 0;
`;

const MonthContainer = styled.div`
  width: 250px; /* Adjust as needed */
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const DayCell = styled.div`
  box-sizing: border-box;
  height: 25px;
  border: 1px solid rgba(211,211,211,0.2);
  padding: 0;
  font-size: 12px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DayHeader = styled.div`
  box-sizing: border-box;
  font-weight: 500;
  text-align: center;
  font-size: 12px;
  padding-bottom: 4px;
`;

const BookingRoundCalendarView: React.FC<BookingRoundCalendarViewProps> = ({
  bookingRound,
  assignments,
  houseOwners,
  fractionsCount,
  selectedAssignments,
  setSelectedAssignments,
}) => {
  const { currentTheme } = useTheme();

  if (!assignments || assignments.length === 0) {
    return <div>No assignments to display in calendar view.</div>;
  }

  const ownerColors = new Map<number, string>();
  const colors = currentTheme === 'dark' ?  [
    '#D63447',  // Darker red (was #FFADAD)
    '#0891B2',  // Darker cyan (was #9BF6FF)
    '#CA8A04',  // Darker yellow (was #FDFFB6)
    '#7C3AED',  // Darker purple (was #BDB2FF)
    '#EA580C',  // Darker orange (was #FFD6A5)
    '#16A34A',  // Darker green (was #CAFFBF)
    '#2563EB',  // Darker blue (was #A0C4FF)
    '#C026D3',  // Darker magenta (was #FFC6FF)
    '#92400E',  // Darker brown (was #D4A5A5)
    '#047857',  // Darker teal (was #B4E7CE)
  ] : [
    '#FFADAD',
    '#9BF6FF',
    '#FDFFB6',
    '#BDB2FF',
    '#FFD6A5',
    '#CAFFBF',
    '#A0C4FF',
    '#FFC6FF',
    '#D4A5A5',
    '#B4E7CE'
  ];
  const uniqueOwners = houseOwners.filter((ho, index, self) =>
    ho.owner && self.findIndex(h => h.owner.id === ho.owner.id) === index
  );

  uniqueOwners.forEach((houseOwner, index) => {
    if (houseOwner.owner) {
      ownerColors.set(houseOwner.owner.id, colors[index % colors.length]);
    }
  });

  const allDates = assignments.flatMap(a => [moment(a.checkin), moment(a.checkout)]);
  const minDate = moment.min(allDates);
  const maxDate = moment.max(allDates);

  const months = [];
  let currentDate = minDate.clone().startOf('month');
  while (currentDate.isSameOrBefore(maxDate, 'month')) {
    months.push(currentDate.clone());
    currentDate.add(1, 'month');
  }

  const assignmentsByDate = new Map<string, IAssignment>();
  for (const assignment of assignments) {
    for (let day = moment(assignment.checkin); day.isSameOrBefore(assignment.checkout, 'day'); day.add(1, 'days')) {
      assignmentsByDate.set(day.format('YYYY-MM-DD'), assignment);
    }
  }

  const renderMonth = (month: moment.Moment) => {
    const monthStart = month.clone().startOf('month');
    const monthEnd = month.clone().endOf('month');
    const startDate = monthStart.clone().startOf('week');
    const endDate = monthEnd.clone().endOf('week');

    const days = [];
    let day = startDate.clone();

    while (day.isSameOrBefore(endDate, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return (
      <MonthContainer key={month.format('YYYY-MM')}>
        <h4 style={{ textAlign: 'center' }}>{month.format('MMMM YYYY')}</h4>
        <MonthGrid>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <DayHeader key={d}>{d}</DayHeader>)}
          {days.map(d => {
            const assignment = assignmentsByDate.get(d.format('YYYY-MM-DD'));
            const owner = assignment ? houseOwners.find(ho => ho.fraction_numbers.includes(assignment.fraction))?.owner : null;
            const isSameMonth = d.isSame(month, 'month');

            const dayStyles: React.CSSProperties = {
              opacity: isSameMonth ? 1 : 0.3,
            };

            if (assignment) {
              const houseOwner = houseOwners.find(ho => ho.fraction_numbers.includes(assignment.fraction));
              const isEmptyFraction = !houseOwner;

              const prevAssignment = assignmentsByDate.get(d.clone().subtract(1, 'days').format('YYYY-MM-DD'));
              const isStart = !prevAssignment || prevAssignment.id !== assignment.id;

              if (isEmptyFraction) {
                dayStyles.backgroundColor = 'rgba(211,211,211,0.3)';
              } else {
                dayStyles.backgroundColor = ownerColors.get(houseOwner.owner.id) || 'transparent';
              }
              dayStyles.position = 'relative';
              dayStyles.zIndex = 1;

              if (!isStart) {
                dayStyles.marginLeft = '-5px';
                dayStyles.borderLeft = 'none';
              }

              const nextAssignment = assignmentsByDate.get(d.clone().add(1, 'days').format('YYYY-MM-DD'));
              const isEnd = !nextAssignment || nextAssignment.id !== assignment.id;

              dayStyles.borderRadius = 0;
              if (isStart) {
                dayStyles.borderTopLeftRadius = '12px';
                dayStyles.borderBottomLeftRadius = '12px';
              }
              if (isEnd) {
                dayStyles.borderTopRightRadius = '12px';
                dayStyles.borderBottomRightRadius = '12px';
              }

              const isSelected = selectedAssignments.some(sa => sa.id === assignment.id);

              if (isSelected) {
                dayStyles.transform = 'scale(1.25)';
                dayStyles.filter = 'saturate(2)';
                dayStyles.boxShadow = '5px 2px 4px 2px rgba(140,140,140,.75)';
                dayStyles.borderTop = '2px solid #3498db';
                dayStyles.borderBottom = '2px solid #3498db';
                if (isStart) {
                  dayStyles.borderLeft = '2px solid #3498db';
                }
                if (isEnd) {
                  dayStyles.borderRight = '2px solid #3498db';
                }
                dayStyles.zIndex = 2;
              } else {
                if (!isStart) {
                  dayStyles.borderLeft = 'none';
                }
              }

              const handleSelect = () => {
                if (bookingRound?.closed) return;

                if (isSelected) {
                  setSelectedAssignments(selectedAssignments.filter(sa => sa.id !== assignment.id));
                } else {
                  if (selectedAssignments.length < 2) {
                    setSelectedAssignments([...selectedAssignments, assignment]);
                  }
                }
              };

              if (bookingRound?.closed) {
                dayStyles.cursor = 'not-allowed';
              }

              const dayCell = (
                <DayCell key={d.format('YYYY-MM-DD')} style={dayStyles} onClick={handleSelect}>
                  {d.format('D')}
                  {assignment.preferred && isStart && (
                    <SmileOutlined
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '490%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '14px',
                        zIndex: 2,
                        color: 'orange'
                      }}
                    />
                  )}
                </DayCell>
              );

              return (
                <Tooltip
                  key={d.format('YYYY-MM-DD')}
                  title={`Fraction #${assignment.fraction}: ${owner?.full_name || 'N/A'}`}
                >
                  {dayCell}
                </Tooltip>
              );
            }

            return (
              <DayCell key={d.format('YYYY-MM-DD')} style={dayStyles}>
                {d.format('D')}
              </DayCell>
            );
          })}
        </MonthGrid>
      </MonthContainer>
    );
  };

  return (
    <ViewContainer>
      <CalendarContainer>
        {months.map(month => renderMonth(month))}
      </CalendarContainer>
      <LegendContainer>
        {uniqueOwners.map(ho => {
          if (!ho.owner) return null;

          const color = ownerColors.get(ho.owner.id);

          return (
            <div key={ho.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{
                minWidth: '20px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: color,
                marginRight: '8px',
                border: '1px solid rgba(211,211,211,0.3)'
              }}></div>
              <div>
                <div>{ho.owner.first_name}</div>
                <div style={{ fontSize: '11px', color: '#888' }}>Fraction #{ho.fraction_numbers.join(', ')}</div>
              </div>
            </div>
          );
        })}
      </LegendContainer>
    </ViewContainer>
  );
};

export default BookingRoundCalendarView;
